import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/Button";

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: 0.55,
    delay: i * 0.07,
    ease: [0.32, 0.72, 0, 1] as const,
  },
});

export function LoginPage() {
  const [params] = useSearchParams();
  // Same rule as RequireAuth: relative paths are fine in production
  // (same-origin SPA), but in dev we need an absolute URL so the OAuth
  // callback bounces back to Vite (:5174) instead of landing on .NET (:7157).
  const rawReturnUrl = params.get("returnUrl");
  const returnUrl = (() => {
    if (rawReturnUrl && /^https?:\/\//i.test(rawReturnUrl)) {
      return rawReturnUrl; // already absolute — trust it (caller chose this)
    }
    const path = rawReturnUrl ?? "/";
    return import.meta.env.DEV ? window.location.origin + path : path;
  })();
  const denied = params.get("denied") === "1";

  useEffect(() => {
    document.getElementById("primary-cta")?.focus();
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Multi-hue pastel background — three soft blooms */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-24 h-[520px] w-[520px] rounded-full bg-butter/40 blur-3xl" />
        <div className="absolute top-20 -right-32 h-[460px] w-[460px] rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-[520px] w-[520px] rounded-full bg-sage/30 blur-3xl" />
      </div>

      <div className="mx-auto flex min-h-screen w-full max-w-[1100px] flex-col px-8 py-10">
        <motion.header {...fade(0)}>
          <Brand size="md" />
        </motion.header>

        <div className="grid flex-1 grid-cols-1 items-center gap-12 py-12 lg:grid-cols-12">
          {/* Left: the pitch */}
          <div className="lg:col-span-7">
            <motion.div {...fade(1)}>
              <span className="inline-flex items-center gap-2 rounded-full bg-surface/80 px-3 py-1 text-[0.78rem] font-medium text-ink-soft shadow-soft backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-sage" />
                Speedy Expediting · NYC DOB
              </span>
            </motion.div>

            <motion.h1
              {...fade(2)}
              className="mt-6 text-[clamp(2.75rem,6vw,4.75rem)] font-medium tracking-tight text-ink"
              style={{ fontVariationSettings: '"wdth" 88, "opsz" 96' }}
            >
              Turn objections into a{" "}
              <span className="text-primary">finished AI1</span>{" "}
              in minutes.
            </motion.h1>

            <motion.p
              {...fade(3)}
              className="mt-6 max-w-xl text-[1.1rem] leading-relaxed text-ink-soft"
            >
              Drop in a plan PDF or objection spreadsheet. The tool extracts
              the address, job number, and objections — you verify, and a
              properly paginated AI1 PDF comes out the other side.
            </motion.p>

            <motion.div
              {...fade(4)}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <form action="/api/auth/login/google" method="get">
                <input type="hidden" name="returnUrl" value={returnUrl} />
                <Button
                  id="primary-cta"
                  type="submit"
                  intent="primary"
                  size="lg"
                  className="min-w-[230px] gap-3"
                >
                  <GoogleGlyph />
                  Sign in with Google
                </Button>
              </form>

              <span className="text-[0.88rem] text-ink-muted">
                Google account required
              </span>
            </motion.div>

            {denied && (
              <motion.div
                {...fade(5)}
                className="mt-8 inline-flex max-w-xl items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3.5"
              >
                <span className="mt-[3px] h-2 w-2 shrink-0 rounded-full bg-danger" />
                <p className="text-[0.92rem] leading-relaxed text-ink">
                  Your Google account is not on the allowlist. Contact the
                  administrator to be added.
                </p>
              </motion.div>
            )}
          </div>

          {/* Right: a compact value card */}
          <motion.aside
            {...fade(3)}
            className="lg:col-span-5"
          >
            <div className="relative rounded-2xl bg-surface p-7 shadow-lift">
              <div className="absolute -top-3 left-7 inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-[0.72rem] font-semibold text-white shadow-soft">
                What's inside
              </div>
              <ul className="space-y-4">
                <Feature
                  tint="primary"
                  title="Drag-and-drop intake"
                  body="PDF, Excel, CSV, or BAX — up to 100 files at once."
                />
                <Feature
                  tint="sage"
                  title="AI-assisted extraction"
                  body="Address, job number, and every objection — verified by you."
                />
                <Feature
                  tint="butter"
                  title="Paginated AI1 output"
                  body="Properly formatted PDF + Excel export, ready to file."
                />
                <Feature
                  tint="sky"
                  title="Saved property memory"
                  body="Common addresses remember their CB, BIN, and applicant."
                />
              </ul>
            </div>
          </motion.aside>
        </div>

        <motion.footer
          {...fade(6)}
          className="flex items-center justify-between border-t border-rule pt-5 text-[0.82rem] text-ink-muted"
        >
          <span>Speedy Expediting · AI1 Assistant</span>
          <span>Internal use only</span>
        </motion.footer>
      </div>
    </div>
  );
}

function Feature({
  tint,
  title,
  body,
}: {
  tint: "primary" | "sage" | "butter" | "sky";
  title: string;
  body: string;
}) {
  const map = {
    primary: { dot: "bg-primary", bg: "bg-primary-soft" },
    sage: { dot: "bg-sage", bg: "bg-sage-soft" },
    butter: { dot: "bg-butter", bg: "bg-butter-soft" },
    sky: { dot: "bg-sky", bg: "bg-sky-soft" },
  }[tint];

  return (
    <li className="flex items-start gap-3.5">
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${map.bg}`}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${map.dot}`} />
      </span>
      <div>
        <div className="text-[0.95rem] font-semibold text-ink">{title}</div>
        <div className="text-[0.88rem] text-ink-soft leading-snug">{body}</div>
      </div>
    </li>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M21.6 12.227c0-.709-.064-1.391-.182-2.045H12v3.868h5.382c-.232 1.25-.937 2.309-1.995 3.018v2.51h3.232c1.891-1.742 2.981-4.305 2.981-7.351z"
        opacity="0.95"
      />
      <path
        fill="currentColor"
        opacity="0.85"
        d="M12 22c2.7 0 4.964-.895 6.619-2.422l-3.232-2.51c-.895.6-2.04.955-3.387.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59C4.71 19.778 8.091 22 12 22z"
      />
      <path
        fill="currentColor"
        opacity="0.75"
        d="M6.405 13.9A5.99 5.99 0 0 1 6.09 12c0-.659.114-1.3.314-1.9V7.51H3.064A9.996 9.996 0 0 0 2 12c0 1.614.386 3.14 1.064 4.49l3.341-2.59z"
      />
      <path
        fill="currentColor"
        opacity="0.65"
        d="M12 5.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C16.96 2.99 14.696 2 12 2 8.091 2 4.71 4.222 3.064 7.51l3.341 2.59C7.19 7.736 9.395 5.977 12 5.977z"
      />
    </svg>
  );
}
